/**
 * @fileoverview Component for displaying sites in a table format.
 * This component includes a search bar, filters, and pagination.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import SiteBigIcon from '../../../assets/site-big.svg'
import {
    useGetSitesCountForStudyIdQuery,
    useGetSitesForStudyIdQuery,
} from '../../../services/sites'
import { formatOutput } from '../../../utils/formatOutput'
import { Search } from '../../elements/Search'
import { Pagination } from '../../Pagination'

/**
 * @function SitesTab
 * @description Component for displaying sites in a table format.
 * @returns {JSX.Element} - A React component that renders the sites.
 */
export function SitesTab() {
    const params = useParams()

    const {
        data: sites,
        isLoading,
        refetch: refetchSites,
    } = useGetSitesForStudyIdQuery(params.studyId)
    const { data: total, refetch: refetchSitesCount } = useGetSitesCountForStudyIdQuery(
        params.studyId
    )

    useEffect(() => {
        refetchSites()
        refetchSitesCount()
    }, [location.search])

    const columns = ['Site', 'Screened', 'Last active']

    function formatDate(timestamp: string): JSX.Element {
        const dt = DateTime.fromISO(timestamp)
        return <span>{dt.toRelative()}</span>
    }

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex sticky top-[177px] bg-white border-b px-8 py-6">
                <Search placeholder="Search by site name..." />
            </div>
            <table className="w-full">
                <thead className="nv-thead top-[266px] sticky">
                    <tr className="h-12">
                        {columns.map((col, index) => (
                            <th
                                key={col}
                                scope="col"
                                className={`${
                                    index === 0 ? 'pl-8 pr-2.5' : 'px-2.5'
                                } text-left text-sm font-medium text-gray-900 whitespace-nowrap`}
                            >
                                <button className="group inline-flex">
                                    {col}
                                    <span className="invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
                                        <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {sites.map((site: Novoic.API.Site) => (
                        <tr key={site.id} className="hover:bg-[#f7f9ff] transition-all">
                            <td
                                className="pl-8 whitespace-nowrap pr-10 py-4 w-[60px] text-sm font-medium"
                                role="presentation"
                            >
                                <div className="flex gap-x-3 items-center">
                                    <SiteBigIcon className="fill-indigo-500 w-8 h-8 stroke-indigo-50" />
                                    <div>
                                        <p className="font-medium">{formatOutput(site.name)}</p>
                                        <p className="text-gray-600 text-xs font-normal">
                                            {formatOutput(site.address)}
                                        </p>
                                    </div>
                                </div>
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm font-medium">
                                {formatOutput(site.screened_count)}
                            </td>
                            <td className="px-2.5 whitespace-nowrap pr-10 py-4 w-[60px] text-sm text-gray-600">
                                {formatDate(site.last_activity_at)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                <Pagination total={total?.count} />
            </div>
        </>
    )
}
